import { useState } from "react";
import { toast } from "sonner";
import { Layers, Plus, Trash2, Check, X, Loader2, Palette } from "lucide-react";
import api, { errMsg } from "@/lib/api";

/**
 * Hotel-only "Unit Types" panel — groups physical units into named types
 * (e.g. "First Floor", "Deluxe Suite") so calendar/reporting can slice by type.
 * Simple multi-select: pick units with checkboxes, name the group, pick a color.
 */
const PALETTE = ["#4F46E5", "#EC4899", "#10B981", "#F59E0B", "#0EA5E9", "#8B5CF6", "#EF4444", "#14B8A6"];

export default function UnitTypesSection({ pid, units, unitTypes = [], reload }) {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [color, setColor] = useState(PALETTE[0]);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Units already grouped in *other* types (so a unit can belong to only one type)
  const takenElsewhere = new Set(unitTypes.flatMap((t) => t.unit_ids || []));
  const togglePick = (uid) => {
    if (takenElsewhere.has(uid)) return;
    setPicked((s) => {
      const n = new Set(s);
      if (n.has(uid)) n.delete(uid); else n.add(uid);
      return n;
    });
  };

  const resetForm = () => { setName(""); setColor(PALETTE[0]); setPicked(new Set()); setCreating(false); };

  const save = async () => {
    if (!name.trim()) return toast.error("Give this type a name — e.g. 'First Floor'");
    if (picked.size === 0) return toast.error("Pick at least one unit for this type");
    setBusy(true);
    try {
      await api.post(`/pms/properties/${pid}/unit-types`, {
        name: name.trim(), color, unit_ids: Array.from(picked),
      });
      toast.success(`'${name.trim()}' created · ${picked.size} unit${picked.size === 1 ? "" : "s"} tagged`);
      resetForm();
      await reload();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const remove = async (tid, tname) => {
    setDeletingId(tid);
    try {
      await api.delete(`/pms/unit-types/${tid}`);
      toast.success(`'${tname}' removed`);
      await reload();
    } catch (e) { toast.error(errMsg(e)); }
    setDeletingId(null);
  };

  return (
    <div data-testid="unit-types-section" className="bg-white rounded-2xl border border-slate-200 p-5 mt-4">
      <div className="flex items-center gap-3">
        <span className="h-9 w-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center"><Layers size={16} /></span>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-bold text-slate-900 text-sm">Unit types <span className="text-[10px] font-semibold uppercase tracking-wider text-indigo-500 bg-indigo-50 border border-indigo-200 rounded-full px-2 py-0.5 ml-1.5 align-middle">hotel</span></h3>
          <p className="text-[11px] text-slate-500 mt-0.5">Group your rooms by floor, category or wing — e.g. tag units 1–10 as <b>First Floor</b>. Optional, but reports and the calendar can slice by these later.</p>
        </div>
        {!creating && (
          <button data-testid="add-unit-type-button" onClick={() => setCreating(true)}
            className="btn-brand rounded-full text-[11px] font-semibold px-3.5 py-2 flex items-center gap-1.5 shrink-0">
            <Plus size={12} /> New type
          </button>
        )}
      </div>

      {unitTypes.length > 0 && (
        <ul className="mt-4 space-y-2" data-testid="unit-types-list">
          {unitTypes.map((t) => {
            const uNames = (t.unit_ids || []).map((uid) => units.find((u) => u.id === uid)?.name).filter(Boolean);
            return (
              <li key={t.id} data-testid={`unit-type-row-${t.name}`}
                className="flex items-center gap-3 border border-slate-200 rounded-xl px-3.5 py-2.5">
                <span className="h-8 w-8 rounded-lg flex items-center justify-center text-[10px] font-bold text-white shrink-0" style={{ backgroundColor: t.color || "#4F46E5" }}>{uNames.length}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-slate-900 truncate">{t.name}</p>
                  <p className="text-[11px] text-slate-500 truncate">{uNames.length ? uNames.join(" · ") : "no units yet"}</p>
                </div>
                <button data-testid={`delete-unit-type-${t.name}`} onClick={() => remove(t.id, t.name)} disabled={deletingId === t.id}
                  className="text-slate-300 hover:text-red-500 shrink-0 disabled:opacity-50">
                  {deletingId === t.id ? <Loader2 className="animate-spin" size={14} /> : <Trash2 size={14} />}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {creating && (
        <div data-testid="unit-type-form" className="mt-4 border-2 border-indigo-200 bg-indigo-50/30 rounded-xl p-4">
          <label className="text-[10px] uppercase font-bold tracking-wider text-indigo-700">Type name</label>
          <input data-testid="unit-type-name-input" autoFocus value={name} onChange={(e) => setName(e.target.value)}
            placeholder="First Floor · Deluxe Wing · Poolside…"
            className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-indigo-500" />

          <div className="mt-3">
            <label className="text-[10px] uppercase font-bold tracking-wider text-indigo-700 flex items-center gap-1.5"><Palette size={11} /> Tag color</label>
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              {PALETTE.map((c) => (
                <button key={c} data-testid={`unit-type-color-${c}`} onClick={() => setColor(c)} type="button"
                  className={`h-7 w-7 rounded-lg border-2 transition-all ${color === c ? "border-slate-900 scale-110" : "border-transparent hover:scale-105"}`}
                  style={{ backgroundColor: c }} aria-label={`Color ${c}`}>
                  {color === c && <Check size={12} className="text-white mx-auto" />}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between">
              <label className="text-[10px] uppercase font-bold tracking-wider text-indigo-700">Pick units ({picked.size} selected)</label>
              {picked.size > 0 && (
                <button data-testid="unit-type-clear-selection" onClick={() => setPicked(new Set())} className="text-[10px] text-slate-500 hover:text-slate-700 underline">clear</button>
              )}
            </div>
            <div className="mt-2 grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-1.5 max-h-52 overflow-y-auto">
              {units.map((u) => {
                const taken = takenElsewhere.has(u.id);
                const on = picked.has(u.id);
                return (
                  <button key={u.id} data-testid={`unit-type-pick-${u.name}`} type="button"
                    disabled={taken} onClick={() => togglePick(u.id)}
                    className={`text-xs font-display font-bold px-1 py-2 rounded-lg border-2 transition-all truncate ${
                      taken ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed"
                            : on ? "border-indigo-600 bg-indigo-600 text-white"
                                 : "border-slate-200 bg-white text-slate-700 hover:border-indigo-400"}`}
                    title={taken ? "Already in another type" : u.name}>
                    {u.name}
                  </button>
                );
              })}
            </div>
            {units.length === 0 && <p className="text-xs text-slate-400 mt-2">No units yet on this property.</p>}
          </div>

          <div className="flex items-center justify-end gap-2 mt-4">
            <button data-testid="unit-type-cancel" onClick={resetForm} disabled={busy}
              className="text-[11px] font-semibold px-3 py-1.5 rounded-full border border-slate-300 text-slate-600 hover:border-slate-500 flex items-center gap-1.5 disabled:opacity-50">
              <X size={11} /> Cancel
            </button>
            <button data-testid="unit-type-save" onClick={save} disabled={busy}
              className="btn-brand text-[11px] font-semibold px-4 py-1.5 rounded-full flex items-center gap-1.5 disabled:opacity-60">
              {busy ? <Loader2 className="animate-spin" size={11} /> : <Check size={11} />} Save type
            </button>
          </div>
        </div>
      )}

      {!creating && unitTypes.length === 0 && (
        <p className="text-[11px] text-slate-400 mt-3">No types yet · optional · you can always add them later.</p>
      )}
    </div>
  );
}
